import { createAsyncThunk } from '@reduxjs/toolkit';
import { QueryClient } from 'react-query';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { CaseByIdQuery, useReassignCaseLeadMutation } from 'src/graphql';
import { TRootState } from 'src/store';
import { CaseLeadUpdateLocation } from 'src/types';
import { getCaseTeamWithoutOldCaseLead } from 'src/utils';
import { caseSlice } from '../Case.slice';

type CaseLeadUpdatedArgs = {
  caseId: string;
  caseLeadId: number | undefined;
  mutateReassignCaseLead: ReturnType<
    typeof useReassignCaseLeadMutation
  >['mutateAsync'];
  queryClient: QueryClient;
};

export const caseLeadUpdated = createAsyncThunk<
  void,
  CaseLeadUpdatedArgs,
  { state: TRootState }
>(
  'case/caseLeadUpdated',
  async (
    {
      caseId,
      caseLeadId,
      mutateReassignCaseLead,
      queryClient,
    }: CaseLeadUpdatedArgs,
    thunkAPI,
  ) => {
    const state = thunkAPI.getState();
    const caseManagers = state.case.caseById?.AllCaseLeads || [];
    const caseSelectedTranslation =
      caseSlice.selectors.caseSelectedTranslation(state);

    const existingCaseLead = state.case.caseById?.CaseLead;
    const newCaseLead = caseManagers.find(
      (manager) => manager.id === caseLeadId,
    );

    const existingCaseTeam = state.case.caseById?.CaseTeam || [];

    if (
      !caseId ||
      caseLeadId === undefined ||
      caseLeadId === existingCaseLead?.id
    ) {
      return;
    }

    const caseQueryKey = [
      'CaseById',
      { id: caseId, translation: caseSelectedTranslation },
    ];

    try {
      queryClient.cancelQueries(caseQueryKey);

      queryClient.setQueryData<CaseByIdQuery | undefined>(
        caseQueryKey,
        (oldData) => {
          if (!oldData?.caseById || !newCaseLead) return oldData;

          const newCaseLeadInTeamPanel = {
            name: newCaseLead.name ?? '',
            description: newCaseLead.site ?? '',
            badgeText: newCaseLead.role,
            id: newCaseLead.id,
            isRowEnabled: true,
            Permissions: newCaseLead.permissions, // Needs fixing to be Case Permissions
            permissions: newCaseLead.permissions,
            invitedAt: new Date().toISOString(),
          };

          const caseTeamWithoutOldCaseLead = getCaseTeamWithoutOldCaseLead(
            oldData,
            existingCaseLead,
          );

          const newCaseTeam = [
            ...(caseTeamWithoutOldCaseLead ?? []),
            newCaseLeadInTeamPanel,
          ];

          return {
            caseById: {
              ...oldData.caseById,
              CaseLead: {
                ...newCaseLead,
              },
              CaseTeam: newCaseTeam,
            },
          };
        },
      );

      await mutateReassignCaseLead(
        {
          caseId,
          newCaseLeadId: caseLeadId,
          location: CaseLeadUpdateLocation.CASE_PAGE,
        },
        {
          onSuccess: () => {
            handleSuccessToast({
              toastMessage: 'Case reassigned',
              toastDescription:
                'The case has been successfully reassigned to a new lead',
            });
          },
          onError: () => {
            handleErrorToast({
              toastMessage: 'Error',
              toastDescription:
                'There was a problem reassigning a new case lead',
            });
          },
        },
      );

      queryClient.invalidateQueries(caseQueryKey);
    } catch (e) {
      queryClient.setQueryData<CaseByIdQuery | undefined>(
        caseQueryKey,
        (oldData) => {
          if (!oldData?.caseById) return oldData;

          return {
            caseById: {
              ...oldData.caseById,
              CaseLead: existingCaseLead
                ? {
                    ...existingCaseLead,
                  }
                : undefined,
              CaseTeam: existingCaseTeam,
            },
          };
        },
      );

      throw e;
    }
  },
);
